import { useState, useEffect } from 'react';
import {
  trpcPricingListPriceZonesWithGeoAreas,
  trpcPricingListPriceZonesWithGeoAreasOpen,
} from '@trashlab/api/src/generated/hooks';
import ZoneMapVisualization from '@trashlab/viteapp/src/components/settings/zone-map-visualization';
import { Polygon } from '@react-google-maps/api';

// Import Shadcn components
import { Skeleton } from '@trashlab/viteapp/src/components/ui/skeleton';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@trashlab/viteapp/src/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface ZoneMapWithGeoAreasProps {
  selectedZoneId?: string | null;
  onZoneSelect?: (zoneId: string) => void;
  isPublic?: boolean;
  tenantId?: string;
}

/**
 * Enhanced zone map that displays both traditional zones (radius and ZIP based)
 * as well as geoareas associated with price zones
 */
const ZoneMapWithGeoAreas = ({
  selectedZoneId: externalSelectedZoneId = null,
  onZoneSelect: externalOnZoneSelect,
  isPublic = false,
  tenantId,
}: ZoneMapWithGeoAreasProps) => {
  const [internalSelectedZoneId, setInternalSelectedZoneId] = useState<
    string | null
  >(externalSelectedZoneId);

  // Keep internal state in sync with external state
  useEffect(() => {
    if (externalSelectedZoneId !== undefined) {
      setInternalSelectedZoneId(externalSelectedZoneId);
    }
  }, [externalSelectedZoneId]);

  // Use either the external selectedZoneId (if provided) or internal state
  const selectedZoneId =
    externalSelectedZoneId !== undefined
      ? externalSelectedZoneId
      : internalSelectedZoneId;

  // Use the auto-generated hook for our query
  const { data, isLoading, error } =
    isPublic && tenantId
      ? trpcPricingListPriceZonesWithGeoAreasOpen.useQuery({
          tenantId,
        })
      : trpcPricingListPriceZonesWithGeoAreas.useQuery(undefined);

  // Handle selection of a zone
  const handleZoneSelect = (zoneId: string) => {
    // If external handler exists, call it
    if (externalOnZoneSelect) {
      externalOnZoneSelect(zoneId);
    } else {
      // Otherwise use internal state
      setInternalSelectedZoneId(
        zoneId === internalSelectedZoneId ? null : zoneId,
      );
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3 p-4 border rounded-lg">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          {error?.message || 'Failed to load price zones data'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="h-full">
      {/* Pass the price zones with their embedded geo areas to the ZoneMapVisualization */}
      <ZoneMapVisualization
        zones={data}
        selectedZoneId={selectedZoneId}
        onZoneSelect={handleZoneSelect}
        isPublic={isPublic}
        tenantId={tenantId}
      />
    </div>
  );
};

export default ZoneMapWithGeoAreas; 